import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import {
  garmentImageUrl,
  createGarmentItem,
  deleteGarmentItem,
  deleteGarmentSample,
  finishGarmentVisibility,
  keepGarmentVisibility,
  listGarmentSample,
  markGarmentSample,
  updateGarmentItem,
  type GarmentItem,
  type GarmentSample,
} from '../../api/garmentLocate';
import { GARMENT_CATEGORY_GROUPS } from '../../data/garmentCategories';

const COLORS = ['#00e0c8', '#ff4d1a', '#3b8bff', '#d946ef', '#ffe14a', '#ff3d8a', '#39d4ff', '#b6ff3b'];
const DRAFT_COLOR = '#ff4d1a';
const VISIBILITY = ['高', '中', '低'] as const;

type Drag =
  | { kind: 'move'; itemId: number; boxIndex: number; dx: number; dy: number; origin: number[] }
  | { kind: 'resize'; itemId: number; boxIndex: number; edge: string; origin: number[] };

type Undo =
  | { kind: 'draft'; draft: { box: number[]; name: string; category: string; visibility: string } }
  | { kind: 'item'; item: GarmentItem }
  | { kind: 'box'; itemId: number; boxIndex: number; box: number[]; name: string; category: string; visibility: string };

type Arm = {
  kind: 'move' | 'resize';
  itemId: number;
  boxIndex: number;
  edge: string;
  dx: number;
  dy: number;
  origin: number[];
  sx: number;
  sy: number;
};

export function LocatePage() {
  const [offset, setOffset] = useState(0);
  const [reload, setReload] = useState(0);
  const [queue, setQueue] = useState<'' | 'risk' | 'visibility' | 'feet'>(() => {
    const saved = sessionStorage.getItem('locate-queue');
    return saved === 'risk' || saved === 'visibility' || saved === 'feet' ? saved : '';
  });
  const [visibilityTotal, setVisibilityTotal] = useState(0);
  const [feetTotal, setFeetTotal] = useState(0);
  const [landOnOpen, setLandOnOpen] = useState(false);
  const [total, setTotal] = useState(0);
  const [sample, setSample] = useState<GarmentSample | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<{ itemId: number; boxIndex: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState(false);
  const [draft, setDraft] = useState<{ box: number[]; name: string; category: string; visibility: string } | null>(null);
  const [pageText, setPageText] = useState('1');
  const [undos, setUndos] = useState<Record<number, Undo[]>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const page = await listGarmentSample(offset, queue, landOnOpen);
        if (cancelled) return;
        setVisibilityTotal(page.visibility_total || 0);
        setFeetTotal(page.feet_total || 0);
        if (queue === 'feet' && !(page.feet_total || 0)) {
          sessionStorage.setItem('locate-queue', '');
          setQueue('');
          setOffset(0);
          return;
        }
        if (landOnOpen) setLandOnOpen(false);
        if (page.offset !== offset) {
          setOffset(page.offset);
          return;
        }
        if (!page.sample && offset > 0) {
          setOffset(Math.max(0, Math.min(offset - 1, page.total - 1)));
          return;
        }
        setTotal(page.total);
        setSample(page.sample);
        const flagged = page.sample?.items.find((item) => item.visibility_note);
        const first = flagged || page.sample?.items[0];
        setSelected(first ? { itemId: first.id, boxIndex: 0 } : null);
        setDraft(null);
        setDrawMode(false);
        setMsg(null);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : '加载失败');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [offset, queue, reload, landOnOpen]);

  useEffect(() => {
    setPageText(total ? String(offset + 1) : '');
  }, [offset, total]);

  const jumpToPage = () => {
    const page = Number(pageText.trim());
    if (!Number.isInteger(page) || page < 1 || page > total) {
      setPageText(total ? String(offset + 1) : '');
      return;
    }
    leaveTo(page - 1);
  };

  const current = sample?.items.find((item) => item.id === selected?.itemId) ?? null;
  const currentRef = useRef(current);
  const draftRef = useRef(draft);
  const sampleRef = useRef(sample);
  const savingRef = useRef(saving);
  const pickedRef = useRef(selected);
  const saveSeq = useRef(0);
  const writeTail = useRef(Promise.resolve());
  const writePending = useRef(0);
  const removeRef = useRef<() => void>(() => {});
  const deletePageRef = useRef<() => void>(() => {});
  const restoreRef = useRef<() => void>(() => {});
  const reviewRef = useRef<() => Promise<void>>(async () => {});
  const reviewedIds = useRef(new Set<number>());
  const reviewFlight = useRef(new Map<number, Promise<void>>());
  const offsetRef = useRef(offset);
  const totalRef = useRef(total);
  offsetRef.current = offset;
  totalRef.current = total;
  const undosRef = useRef(undos);
  undosRef.current = undos;
  currentRef.current = current;
  draftRef.current = draft;
  sampleRef.current = sample;
  savingRef.current = saving;
  pickedRef.current = selected;

  reviewRef.current = async () => {
    const page = sampleRef.current;
    if (!page || page.qc_status === 'ok') return;
    const pending = reviewFlight.current.get(page.id);
    if (pending) return pending;
    const job = (async () => {
      if (reviewedIds.current.has(page.id)) return;
      reviewedIds.current.add(page.id);
      sampleRef.current = { ...page, qc_status: 'ok' };
      setSample((prev) => (prev && prev.id === page.id ? { ...prev, qc_status: 'ok' } : prev));
      try {
        await markGarmentSample(page.id);
      } catch (e) {
        reviewedIds.current.delete(page.id);
        setMsg(e instanceof Error ? e.message : '质检标记失败');
        setReload((value) => value + 1);
      } finally {
        reviewFlight.current.delete(page.id);
      }
    })();
    reviewFlight.current.set(page.id, job);
    return job;
  };

  const leaveTo = (next: number) => {
    if (next === offsetRef.current) return;
    setOffset(next);
  };

  const saveDraft = async () => {
    const pending = draftRef.current;
    const page = sampleRef.current;
    if (!pending || !page || savingRef.current) return;
    const name = pending.name.trim();
    const category = pending.category.trim();
    if (!name || !category) {
      setMsg('先填名字和品类');
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setMsg(null);
    try {
      const saved = await createGarmentItem(page.id, {
        name,
        category,
        visibility: pending.visibility,
        boxes: [pending.box],
      });
      setSample((prev) => prev ? { ...prev, items: [...prev.items, saved] } : prev);
      setSelected({ itemId: saved.id, boxIndex: 0 });
      setDraft(null);
      setMsg('已保存');
      await reviewRef.current();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '保存失败');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const updateDraft = (next: { box: number[]; name: string; category: string; visibility: string }) => {
    draftRef.current = next;
    setDraft(next);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      const typing = !!target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable);
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (draftRef.current) {
          void saveDraft();
          return;
        }
        const item = currentRef.current;
        if (item) void save(item);
        return;
      }
      if (typing || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'Backspace') {
        event.preventDefault();
        removeRef.current();
        return;
      }
      if (event.key === 'Delete') {
        event.preventDefault();
        deletePageRef.current();
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        const next = offsetRef.current + 1;
        if (next < totalRef.current) leaveTo(next);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const next = offsetRef.current - 1;
        if (next >= 0) leaveTo(next);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [total]);

  const patchItem = (itemId: number, next: Partial<GarmentItem>) => {
    setSample((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((item) => (item.id === itemId ? { ...item, ...next } : item)),
      };
    });
  };

  const enqueueWrite = (task: () => Promise<unknown>) => {
    writePending.current += 1;
    savingRef.current = true;
    setSaving(true);
    const run = writeTail.current.then(task, task);
    writeTail.current = run.then(() => {
      writePending.current -= 1;
      if (writePending.current === 0) {
        savingRef.current = false;
        setSaving(false);
      }
    }, () => {
      writePending.current -= 1;
      if (writePending.current === 0) {
        savingRef.current = false;
        setSaving(false);
      }
    });
    return run;
  };

  const save = (item: GarmentItem) => {
    const seq = ++saveSeq.current;
    const sent = item.boxes.map((box) => box.join(',')).join('|');
    setMsg(null);
    return enqueueWrite(async () => {
      try {
        const saved = await updateGarmentItem(item.id, {
          name: item.name,
          category: item.category,
          visibility: item.visibility,
          boxes: item.boxes,
        });
        if (seq !== saveSeq.current) return;
        const latest = sampleRef.current?.items.find((entry) => entry.id === item.id);
        const now = latest?.boxes.map((box) => box.join(',')).join('|');
        if (latest && now === sent) patchItem(item.id, saved);
        setMsg('已保存');
        await reviewRef.current();
      } catch (e) {
        if (seq !== saveSeq.current) return;
        setMsg(e instanceof Error ? e.message : '保存失败');
      }
    });
  };

  const pushUndo = (entry: Undo) => {
    const pageId = sampleRef.current?.id;
    if (!pageId) return;
    setUndos((prev) => ({ ...prev, [pageId]: [...(prev[pageId] ?? []), entry] }));
  };

  removeRef.current = () => {
    const page = sampleRef.current;
    if (!page) return;
    const pending = draftRef.current;
    if (pending) {
      pushUndo({ kind: 'draft', draft: pending });
      setDraft(null);
      setMsg('已删除');
      return;
    }
    const picked = pickedRef.current;
    const item = page.items.find((entry) => entry.id === picked?.itemId);
    if (!item || !picked) return;
    const box = item.boxes[picked.boxIndex];
    if (!box) return;
    if (item.boxes.length <= 1) {
      const nextPage = { ...page, items: page.items.filter((entry) => entry.id !== item.id) };
      const next = nextPage.items[0];
      const nextSel = next ? { itemId: next.id, boxIndex: 0 } : null;
      sampleRef.current = nextPage;
      pickedRef.current = nextSel;
      currentRef.current = next ?? null;
      setSample(nextPage);
      setSelected(nextSel);
      pushUndo({ kind: 'item', item });
      setMsg('已删除');
      void enqueueWrite(async () => {
        try {
          await deleteGarmentItem(item.id);
          await reviewRef.current();
        } catch (e) {
          if (sampleRef.current?.id === page.id) {
            sampleRef.current = page;
            setSample(page);
          }
          setMsg(e instanceof Error ? e.message : '删除失败');
        }
      });
      return;
    }
    const boxes = item.boxes.filter((_, index) => index !== picked.boxIndex);
    const nextItem = { ...item, boxes };
    const nextPage = { ...page, items: page.items.map((entry) => (entry.id === item.id ? nextItem : entry)) };
    const nextSel = { itemId: item.id, boxIndex: Math.min(picked.boxIndex, boxes.length - 1) };
    sampleRef.current = nextPage;
    pickedRef.current = nextSel;
    currentRef.current = nextItem;
    setSample(nextPage);
    setSelected(nextSel);
    pushUndo({
      kind: 'box',
      itemId: item.id,
      boxIndex: picked.boxIndex,
      box,
      name: item.name,
      category: item.category,
      visibility: item.visibility,
    });
    setMsg('已删除');
    void enqueueWrite(async () => {
      try {
        await updateGarmentItem(item.id, {
          name: item.name,
          category: item.category,
          visibility: item.visibility,
          boxes,
        });
        await reviewRef.current();
      } catch (e) {
        if (sampleRef.current?.id === page.id) {
          sampleRef.current = page;
          setSample(page);
        }
        setMsg(e instanceof Error ? e.message : '删除失败');
      }
    });
  };

  restoreRef.current = () => {
    const page = sampleRef.current;
    if (!page || savingRef.current) return;
    const stack = undosRef.current[page.id] ?? [];
    const entry = stack[stack.length - 1];
    if (!entry) return;
    setUndos((prev) => ({ ...prev, [page.id]: (prev[page.id] ?? []).slice(0, -1) }));
    if (entry.kind === 'draft') {
      setDraft(entry.draft);
      setDrawMode(false);
      setSelected(null);
      setMsg('已恢复');
      return;
    }
    const putBack = () => setUndos((prev) => ({ ...prev, [page.id]: [...(prev[page.id] ?? []), entry] }));
    setSaving(true);
    setMsg(null);
    if (entry.kind === 'item') {
      void createGarmentItem(page.id, {
        name: entry.item.name,
        category: entry.item.category,
        visibility: entry.item.visibility,
        boxes: entry.item.boxes,
        models: entry.item.models,
      }).then((saved) => {
        setSample((currentPage) => currentPage && currentPage.id === page.id
          ? { ...currentPage, items: [...currentPage.items, saved] }
          : currentPage);
        setSelected({ itemId: saved.id, boxIndex: 0 });
        setMsg('已恢复');
      }).catch((e) => {
        putBack();
        setMsg(e instanceof Error ? e.message : '恢复失败');
      }).finally(() => setSaving(false));
      return;
    }
    const host = page.items.find((item) => item.id === entry.itemId);
    const restorePromise = host
      ? updateGarmentItem(host.id, {
        name: host.name,
        category: host.category,
        visibility: host.visibility,
        boxes: [
          ...host.boxes.slice(0, entry.boxIndex),
          entry.box,
          ...host.boxes.slice(entry.boxIndex),
        ],
      }).then((saved) => {
        patchItem(host.id, saved);
        setSelected({ itemId: host.id, boxIndex: entry.boxIndex });
      })
      : createGarmentItem(page.id, {
        name: entry.name,
        category: entry.category,
        visibility: entry.visibility,
        boxes: [entry.box],
      }).then((saved) => {
        setSample((currentPage) => currentPage && currentPage.id === page.id
          ? { ...currentPage, items: [...currentPage.items, saved] }
          : currentPage);
        setSelected({ itemId: saved.id, boxIndex: 0 });
      });
    void restorePromise.then(() => {
      setMsg('已恢复');
    }).catch((e) => {
      putBack();
      setMsg(e instanceof Error ? e.message : '恢复失败');
    }).finally(() => setSaving(false));
  };

  const deletePage = () => {
    const page = sampleRef.current;
    if (!page || savingRef.current) return;
    setSaving(true);
    setMsg(null);
    void deleteGarmentSample(page.id).then(() => {
      setMsg('已删除这张');
      setReload((value) => value + 1);
    }).catch((error) => {
      setMsg(error instanceof Error ? error.message : '删除失败');
    }).finally(() => setSaving(false));
  };
  deletePageRef.current = deletePage;

  const undoCount = sample ? (undos[sample.id]?.length ?? 0) : 0;
  const footConflict = sample ? footNameConflict(sample.items) : [];
  const statusLabel = sample?.qc_status === 'ok'
    ? '质检通过'
    : footConflict.length > 0
      ? `${footConflict.join('、')}有多个名字`
      : sample
        ? sampleHint(sample.items)
        : '';
  const statusTone = sample?.qc_status === 'ok'
    ? 'bg-[#e7f8ee] text-[#0d7a3a]'
    : footConflict.length > 0
      ? 'bg-[#ffece6] text-[#9a3412]'
      : 'bg-[#fff6dc] text-[#8a5a00]';

  return (
    <div className="h-full grid grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0 flex flex-col">
        <div className="shrink-0 border-b border-[var(--line)] bg-[var(--paper)]">
          <div className="flex items-center gap-2 px-3 pt-2 overflow-x-auto">
            <div className="flex items-center shrink-0 rounded-[var(--radius-sm)] border border-[var(--line)] bg-[var(--bg)]">
              <button className="btn-ghost shrink-0 whitespace-nowrap rounded-r-none px-2" disabled={offset <= 0} onClick={() => leaveTo(offset - 1)} aria-label="上一张">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <form
                className="flex items-center gap-1 px-1 text-sm text-[var(--muted)] border-x border-[var(--line)]"
                onSubmit={(event) => {
                  event.preventDefault();
                  jumpToPage();
                }}
              >
                <input
                  aria-label="页码"
                  inputMode="numeric"
                  className="w-12 bg-transparent py-1 text-right text-sm tabular-nums text-[var(--ink)] outline-none"
                  value={pageText}
                  onChange={(event) => setPageText(event.target.value.replace(/[^\d]/g, ''))}
                  onBlur={jumpToPage}
                />
                <span className="tabular-nums pr-1">/ {total || 0}</span>
              </form>
              <button className="btn-ghost shrink-0 whitespace-nowrap rounded-l-none px-2" disabled={offset + 1 >= total} onClick={() => leaveTo(offset + 1)} aria-label="下一张">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <button
              className={`btn-ghost shrink-0 whitespace-nowrap text-sm ${queue === 'risk' ? 'bg-[#ffece6] text-[#9a3412]' : ''}`}
              type="button"
              onClick={() => {
                const next = queue === 'risk' ? '' : 'risk';
                sessionStorage.setItem('locate-queue', next);
                setQueue(next);
                setOffset(0);
                setLandOnOpen(true);
              }}
            >
              {queue === 'risk' ? '高危' : '只看高危'}
            </button>
            <button
              className={`btn-ghost shrink-0 whitespace-nowrap text-sm ${queue === 'visibility' ? 'bg-[#fff6dc] text-[#8a5a00]' : ''}`}
              type="button"
              onClick={() => {
                const next = queue === 'visibility' ? '' : 'visibility';
                sessionStorage.setItem('locate-queue', next);
                setQueue(next);
                setOffset(0);
                setLandOnOpen(false);
              }}
            >
              {queue === 'visibility' ? `可见度 ${visibilityTotal}` : `只看可见度 ${visibilityTotal}`}
            </button>
            <button
              className={`btn-ghost shrink-0 whitespace-nowrap text-sm ${queue === 'feet' ? 'bg-[#fff6dc] text-[#8a5a00]' : ''}`}
              type="button"
              disabled={feetTotal === 0}
              onClick={() => {
                const next = queue === 'feet' ? '' : 'feet';
                sessionStorage.setItem('locate-queue', next);
                setQueue(next);
                setOffset(0);
                setLandOnOpen(next === 'feet');
              }}
            >
              {feetTotal === 0 ? '已标完' : `待标注 ${feetTotal}`}
            </button>
            <div className="flex-1" />
            <button
              className={`btn-ghost shrink-0 whitespace-nowrap text-sm ${drawMode ? 'bg-[var(--accent-soft)] text-[var(--ink)]' : ''}`}
              onClick={() => {
                setDrawMode((value) => !value);
                setMsg(null);
              }}
            >
              {drawMode ? '取消画框' : '新建框'}
            </button>
            <button className="btn-ghost shrink-0 whitespace-nowrap text-sm" type="button" disabled={undoCount === 0 || saving} onClick={() => restoreRef.current()}>
              恢复删除
            </button>
            <button className="btn-ghost shrink-0 whitespace-nowrap text-sm text-[#9a3412]" type="button" title="Delete" disabled={!sample || saving} onClick={() => deletePageRef.current()}>
              删除这张
            </button>
          </div>
          <div className="flex items-center gap-2 px-3 pt-1.5 pb-2 min-w-0">
            {statusLabel && (
              <span className={`shrink-0 whitespace-nowrap text-xs px-2 py-0.5 rounded-full ${statusTone}`}>{statusLabel}</span>
            )}
            <span className="min-w-0 truncate text-xs text-[var(--muted)]">{sample?.sample_key}</span>
            <span className="ml-auto shrink-0 flex items-center gap-2 text-xs text-[var(--muted)] whitespace-nowrap">
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {msg && <span className="text-[var(--ink)]">{msg}</span>}
              <span>方向键翻页</span>
            </span>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4 bg-[var(--bg)]">
          {loading && <div className="text-sm text-[var(--muted)]">正在读取…</div>}
          {error && <div className="text-sm text-[var(--red)]">{error}</div>}
          {sample && (
            <LocateCanvas
              key={sample.id}
              sampleId={sample.id}
              items={sample.items}
              selected={selected}
              onSelect={setSelected}
              onPreview={(itemId, boxes) => patchItem(itemId, { boxes })}
              onCommit={(itemId, boxes) => {
                const item = sampleRef.current?.items.find((entry) => entry.id === itemId);
                if (!item) return;
                const next = { ...item, boxes };
                patchItem(itemId, { boxes });
                void save(next);
              }}
              drawMode={drawMode}
              draftBox={draft?.box ?? null}
              onDraw={(box) => {
                setDraft({ box, name: '', category: '', visibility: '中' });
                setDrawMode(false);
                setSelected(null);
              }}
            />
          )}
        </div>
      </div>
      <aside className="border-l border-[var(--line)] bg-[var(--paper)] overflow-auto p-4">
        <h2 className="text-sm font-semibold mb-3">衣服定位</h2>
        {queue === 'feet' && (
          <p className="text-xs text-[var(--muted)] leading-relaxed mb-3">
            这里是筛出来的鞋和袜子。质检之后还留在这一栏，左右翻页都能看到结果。
          </p>
        )}
        {queue === 'visibility' && (
          <p className="text-xs text-[var(--muted)] leading-relaxed mb-3">
            可见度只看框里实际露出来的部分。只露出领口、袖口、下摆，或配饰框小到看不清，算低。鞋头鞋面清楚但鞋帮看不到，算中。画面模糊再降一档。标黄的是两个模型档位不一致的框。
          </p>
        )}
        {drawMode && <p className="text-sm text-[var(--muted)] mb-3">在图上按住拖出一个新框。</p>}
        {draft && (
          <form
            className="space-y-3 mb-6"
            onSubmit={(event) => {
              event.preventDefault();
              void saveDraft();
            }}
          >
            <div className="text-xs font-semibold">新框</div>
            <label className="block text-xs text-[var(--muted)]">
              名字
              <input
                className="mt-1 w-full border border-[var(--line)] rounded-[var(--radius-sm)] px-2 py-1.5 text-sm text-[var(--ink)]"
                value={draft.name}
                onChange={(event) => updateDraft({ ...draft, name: event.target.value })}
                onBlur={() => {
                  if (draftRef.current?.name.trim() && draftRef.current.category.trim()) void saveDraft();
                }}
              />
            </label>
            <label className="block text-xs text-[var(--muted)]">
              品类
              <CategorySelect
                value={draft.category}
                onChange={(category) => {
                  const next = { ...draft, category };
                  updateDraft(next);
                  if (next.name.trim() && next.category.trim()) void saveDraft();
                }}
              />
            </label>
            <div>
              <div className="text-xs text-[var(--muted)] mb-1">可见度</div>
              <div className="flex gap-2">
                {VISIBILITY.map((level) => (
                  <button
                    key={level}
                    type="button"
                    className={`btn-ghost text-sm ${draft.visibility === level ? 'bg-[var(--accent-soft)] text-[var(--ink)]' : ''}`}
                    onClick={() => updateDraft({ ...draft, visibility: level })}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button className="btn-primary text-sm" type="submit" disabled={saving || !draft.name.trim() || !draft.category.trim()}>保存 Ctrl+S</button>
              <button className="btn-ghost text-sm" type="button" onClick={() => setDraft(null)}>取消</button>
            </div>
          </form>
        )}
        {!current && !draft && <p className="text-sm text-[var(--muted)]">这张图没有待改的单品。点「新建框」可以补一个。</p>}
        {current && !draft && (
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              void save(current);
            }}
          >
            <label className="block text-xs text-[var(--muted)]">
              名字
              <input
                className="mt-1 w-full border border-[var(--line)] rounded-[var(--radius-sm)] px-2 py-1.5 text-sm text-[var(--ink)]"
                value={current.name}
                onChange={(event) => patchItem(current.id, { name: event.target.value })}
                onBlur={(event) => void save({ ...current, name: event.target.value })}
              />
            </label>
            <label className="block text-xs text-[var(--muted)]">
              品类
              <CategorySelect
                value={current.category}
                onChange={(category) => {
                  const next = { ...current, category };
                  patchItem(current.id, { category });
                  void save(next);
                }}
              />
            </label>
            <div>
              <div className="text-xs text-[var(--muted)] mb-1">可见度</div>
              <div className="flex gap-2">
                {VISIBILITY.map((level) => (
                  <button
                    key={level}
                    type="button"
                    className={`btn-ghost text-sm ${current.visibility === level ? 'bg-[var(--accent-soft)] text-[var(--ink)]' : ''}`}
                    onClick={() => {
                      const next = { ...current, visibility: level, visibility_note: '' };
                      patchItem(current.id, { visibility: level, visibility_note: '' });
                      void save(next);
                    }}
                  >
                    {level}
                  </button>
                ))}
              </div>
            </div>
            {current.visibility_note && (
              <div className="text-xs leading-relaxed border border-[#e6d3a1] bg-[#fff6dc] text-[#6b4e16] rounded-[var(--radius-sm)] p-2">
                {current.visibility_note}
                <button
                  className="btn-ghost text-xs mt-2"
                  type="button"
                  disabled={saving}
                  onClick={() => {
                    const itemId = current.id;
                    patchItem(itemId, { visibility_note: '' });
                    void enqueueWrite(async () => {
                      try {
                        await keepGarmentVisibility(itemId);
                        setMsg('维持当前可见度');
                      } catch (e) {
                        setMsg(e instanceof Error ? e.message : '维持失败');
                      }
                    });
                  }}
                >
                  维持当前
                </button>
              </div>
            )}
            <p className="text-xs text-[var(--muted)] leading-relaxed">{current.reason || '没有理由'}</p>
            <p className="text-xs text-[var(--muted)]">{modelLine(current.models)}</p>
            <p className="text-xs text-[var(--muted)]">
              {current.boxes.length > 1 ? `${current.boxes.length} 个框。松开或删除都会自动保存` : '松开或删除都会自动保存'}
            </p>
            <button className="btn-primary text-sm" type="submit" disabled={saving}>保存 Ctrl+S</button>
          </form>
        )}
        {queue === 'visibility' && sample && (
          <button
            className="btn-primary text-sm mt-4"
            type="button"
            disabled={saving}
            onClick={() => {
              const page = sample;
              void enqueueWrite(async () => {
                try {
                  await finishGarmentVisibility(page.id);
                  setMsg('这张可见度已看完');
                  setReload((value) => value + 1);
                } catch (e) {
                  setMsg(e instanceof Error ? e.message : '提交失败');
                }
              });
            }}
          >
            这张看完
          </button>
        )}
        <ul className="mt-6 space-y-2">
          {sample?.items.map((item, index) => (
            <li key={item.id}>
              <button
                type="button"
                className={`w-full text-left text-xs px-2 py-1.5 rounded-[var(--radius-sm)] border ${item.visibility_note ? 'bg-[#fff6dc] ' : ''}${selected?.itemId === item.id ? 'border-[var(--ink)]' : 'border-[var(--line)]'}`}
                onClick={() => setSelected({ itemId: item.id, boxIndex: 0 })}
              >
                <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: COLORS[index % COLORS.length] }} />
                {item.name}
                <span className="block pl-4 text-[10px] text-[var(--muted)]">{itemHint(item, sample?.items ?? [])}</span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}

const RISKY_L1 = new Set(['shoes', 'jewelry_watches', 'accessories', 'bags', 'intimates_hosiery']);

function isShoe(category: string) {
  return category.startsWith('shoes.');
}

function isSock(category: string) {
  return category.includes('sock');
}

function footNameConflict(items: { name: string; category: string }[]) {
  const notes: string[] = [];
  if (new Set(items.filter((item) => isShoe(item.category)).map((item) => item.name)).size > 1) notes.push('鞋');
  if (new Set(items.filter((item) => isSock(item.category)).map((item) => item.name)).size > 1) notes.push('袜子');
  return notes;
}

function itemHint(item: { name: string; category: string; models?: string[] }, items: { name: string; category: string }[]) {
  const models = item.models ?? [];
  const conflict = (isShoe(item.category) && footNameConflict(items).includes('鞋'))
    || (isSock(item.category) && footNameConflict(items).includes('袜子'));
  if (conflict) return `名字和其他${isSock(item.category) ? '袜子' : '鞋'}不一致`;
  const risky = RISKY_L1.has(item.category.split('.')[0]);
  if (models.length === 0) return '人工补的框';
  if (models.length < 4) return `只有 ${models.join('、')}，建议核对`;
  if (risky) return `四家里的小件，建议看框 · ${models.join('、')}`;
  return `四家一致 · ${models.join('、')}`;
}

function sampleHint(items: { category: string; models?: string[] }[]) {
  if (items.some((item) => (item.models ?? []).length < 4)) return '有兜底框，建议核对';
  if (items.some((item) => RISKY_L1.has(item.category.split('.')[0]))) return '有鞋或小件，建议看框';
  return '四家一致，可抽查';
}

function modelLine(models: string[] | undefined) {
  if (!models || models.length === 0) return '人工补的框';
  return `综合了 ${models.join('、')}`;
}

function CategorySelect({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const known = GARMENT_CATEGORY_GROUPS.some((group) => group.items.some((item) => item.id === value));
  return (
    <select
      className="mt-1 w-full border border-[var(--line)] rounded-[var(--radius-sm)] bg-[var(--paper)] px-2 py-1.5 text-sm text-[var(--ink)]"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">选择品类</option>
      {value && !known && <option value={value}>{value}</option>}
      {GARMENT_CATEGORY_GROUPS.map((group) => (
        <optgroup key={group.id} label={`${group.label} ${group.id}`}>
          {group.items.map((item) => (
            <option key={item.id} value={item.id}>{item.label} {item.id}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

function LocateCanvas({
  sampleId,
  items,
  selected,
  onSelect,
  onPreview,
  onCommit,
  drawMode,
  draftBox,
  onDraw,
}: {
  sampleId: number;
  items: GarmentItem[];
  selected: { itemId: number; boxIndex: number } | null;
  onSelect: (value: { itemId: number; boxIndex: number }) => void;
  onPreview: (itemId: number, boxes: number[][]) => void;
  onCommit: (itemId: number, boxes: number[][]) => void;
  drawMode: boolean;
  draftBox: number[] | null;
  onDraw: (box: number[]) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const armRef = useRef<Arm | null>(null);
  const drawRef = useRef<{ x: number; y: number; sx: number; sy: number; box: number[] } | null>(null);
  const drawModeRef = useRef(drawMode);
  drawModeRef.current = drawMode;
  const itemsRef = useRef(items);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  itemsRef.current = items;
  const [frame, setFrame] = useState<{ id: number; image: HTMLImageElement } | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const image = frame?.id === sampleId ? frame.image : null;

  useEffect(() => {
    let cancelled = false;
    setImageError(null);
    const next = new Image();
    next.onload = () => {
      if (!cancelled) setFrame({ id: sampleId, image: next });
    };
    next.onerror = () => {
      if (!cancelled) setImageError('图片没有读出来');
    };
    next.src = garmentImageUrl(sampleId);
    return () => {
      cancelled = true;
    };
  }, [sampleId]);

  const paint = useMemo(() => {
    return () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (!image) {
        const blank = canvas.getContext('2d');
        blank?.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(image, 0, 0);
      const width = canvas.width;
      const height = canvas.height;
      items.forEach((item, index) => {
        const color = COLORS[index % COLORS.length];
        item.boxes.forEach((box, boxIndex) => {
          const active = selected?.itemId === item.id && selected.boxIndex === boxIndex;
          const [x1, y1, x2, y2] = box;
          const scale = screenScale(canvas);
          strokeBox(ctx, color, x1 * width, y1 * height, (x2 - x1) * width, (y2 - y1) * height, (active ? 2.6 : 2) * scale, scale);
          if (active && !drawMode) {
            const grip = 7 * scale;
            for (const [, hx, hy] of handles(box)) {
              ctx.fillStyle = '#fff';
              ctx.lineWidth = 1.15 * scale;
              ctx.fillRect(hx * width - grip / 2, hy * height - grip / 2, grip, grip);
              ctx.strokeRect(hx * width - grip / 2, hy * height - grip / 2, grip, grip);
            }
          }
        });
      });
      if (draftBox) {
        const [x1, y1, x2, y2] = draftBox;
        const scale = screenScale(canvas);
        ctx.save();
        ctx.setLineDash([6 * scale, 4 * scale]);
        strokeBox(ctx, DRAFT_COLOR, x1 * width, y1 * height, (x2 - x1) * width, (y2 - y1) * height, 2.4 * scale, scale);
        ctx.restore();
      }
    };
  }, [image, items, selected, draftBox, drawMode]);

  useEffect(() => {
    paint();
  }, [paint]);

  const point = (event: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    };
  };

  return (
    <div className="w-full max-w-3xl">
      {!image && !imageError && <div className="text-sm text-[var(--muted)] mb-2">正在读取图片…</div>}
      {imageError && <div className="text-sm text-[var(--red)] mb-2">{imageError}</div>}
    <canvas
      ref={canvasRef}
      className="w-full max-w-3xl h-auto bg-[#efe6d8] rounded-[var(--radius)] touch-none"
      style={{ cursor: drawMode ? 'crosshair' : undefined }}
      onPointerDown={(event) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.setPointerCapture(event.pointerId);
        const at = point(event);
        if (drawModeRef.current) {
          drawRef.current = { x: at.x, y: at.y, sx: event.clientX, sy: event.clientY, box: [at.x, at.y, at.x, at.y] };
          return;
        }
        const hit = hitTest(itemsRef.current, at, canvas, selectedRef.current);
        if (!hit) return;
        const already = selectedRef.current?.itemId === hit.item.id && selectedRef.current.boxIndex === hit.boxIndex;
        onSelect({ itemId: hit.item.id, boxIndex: hit.boxIndex });
        if (!already) return;
        const box = hit.item.boxes[hit.boxIndex];
        armRef.current = {
          kind: hit.edge ? 'resize' : 'move',
          itemId: hit.item.id,
          boxIndex: hit.boxIndex,
          edge: hit.edge,
          dx: at.x - box[0],
          dy: at.y - box[1],
          origin: [...box],
          sx: event.clientX,
          sy: event.clientY,
        };
      }}
      onPointerMove={(event) => {
        const canvas = canvasRef.current;
        const drawing = drawRef.current;
        if (drawing && canvas) {
          const at = point(event);
          drawing.box = [Math.min(drawing.x, at.x), Math.min(drawing.y, at.y), Math.max(drawing.x, at.x), Math.max(drawing.y, at.y)];
          const ctx = canvas.getContext('2d');
          if (ctx && image) {
            paint();
            const scale = screenScale(canvas);
            const [x1, y1, x2, y2] = drawing.box;
            ctx.save();
            ctx.setLineDash([6 * scale, 4 * scale]);
            strokeBox(ctx, DRAFT_COLOR, x1 * canvas.width, y1 * canvas.height, (x2 - x1) * canvas.width, (y2 - y1) * canvas.height, 2.4 * scale, scale);
            ctx.restore();
          }
          return;
        }
        const arm = armRef.current;
        if (arm && !dragRef.current) {
          const dist = Math.hypot(event.clientX - arm.sx, event.clientY - arm.sy);
          if (dist < 6) return;
          dragRef.current = arm.kind === 'resize'
            ? { kind: 'resize', itemId: arm.itemId, boxIndex: arm.boxIndex, edge: arm.edge, origin: arm.origin }
            : { kind: 'move', itemId: arm.itemId, boxIndex: arm.boxIndex, dx: arm.dx, dy: arm.dy, origin: arm.origin };
        }
        const drag = dragRef.current;
        if (!drag) {
          if (!canvas) return;
          const hit = hitTest(itemsRef.current, point(event), canvas, selectedRef.current);
          canvas.style.cursor = hit ? cursorFor(hit.edge, hit.item.id === selectedRef.current?.itemId && hit.boxIndex === selectedRef.current?.boxIndex) : 'default';
          return;
        }
        const at = point(event);
        const item = itemsRef.current.find((entry) => entry.id === drag.itemId);
        if (!item) return;
        const boxes = item.boxes.map((box, index) => (index === drag.boxIndex ? movedBox(drag, at) : box));
        onPreview(drag.itemId, boxes);
      }}
      onPointerUp={(event) => {
        const drawing = drawRef.current;
        if (drawing) {
          drawRef.current = null;
          const at = point(event);
          const box = [Math.min(drawing.x, at.x), Math.min(drawing.y, at.y), Math.max(drawing.x, at.x), Math.max(drawing.y, at.y)].map(round);
          const moved = Math.hypot(event.clientX - drawing.sx, event.clientY - drawing.sy);
          if (moved >= 6 && box[2] - box[0] >= 0.01 && box[3] - box[1] >= 0.01) onDraw(box);
          return;
        }
        const drag = dragRef.current;
        dragRef.current = null;
        armRef.current = null;
        if (!drag) return;
        const item = itemsRef.current.find((entry) => entry.id === drag.itemId);
        if (!item) return;
        const at = point(event);
        const boxes = item.boxes.map((box, index) => (index === drag.boxIndex ? movedBox(drag, at) : box));
        const changed = drag.origin.some((value, corner) => Math.abs(value - boxes[drag.boxIndex][corner]) >= 0.0005);
        if (!changed) return;
        onCommit(drag.itemId, boxes);
      }}
    />
    </div>
  );
}

function strokeBox(
  ctx: CanvasRenderingContext2D,
  color: string,
  x: number,
  y: number,
  w: number,
  h: number,
  width: number,
  scale: number,
) {
  ctx.lineJoin = 'miter';
  ctx.strokeStyle = 'rgba(0,0,0,0.88)';
  ctx.lineWidth = width + 1.4 * scale;
  ctx.strokeRect(x, y, w, h);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.strokeRect(x, y, w, h);
}

function screenScale(canvas: HTMLCanvasElement) {
  const rect = canvas.getBoundingClientRect();
  return rect.width > 0 ? canvas.width / rect.width : 1;
}

function cursorFor(edge: string, selected: boolean) {
  if (!selected) return 'default';
  if (!edge) return 'grab';
  if (edge === 'n' || edge === 's') return 'ns-resize';
  if (edge === 'e' || edge === 'w') return 'ew-resize';
  if (edge === 'nw' || edge === 'se') return 'nwse-resize';
  if (edge === 'ne' || edge === 'sw') return 'nesw-resize';
  return 'default';
}

function handles(box: number[]) {
  const [x1, y1, x2, y2] = box;
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  return [
    ['nw', x1, y1],
    ['n', mx, y1],
    ['ne', x2, y1],
    ['w', x1, my],
    ['e', x2, my],
    ['sw', x1, y2],
    ['s', mx, y2],
    ['se', x2, y2],
  ] as const;
}

function hitTest(
  items: GarmentItem[],
  at: { x: number; y: number },
  canvas: HTMLCanvasElement,
  selected: { itemId: number; boxIndex: number } | null,
) {
  const rect = canvas.getBoundingClientRect();
  const handleX = 6 / Math.max(rect.width, 1);
  const handleY = 6 / Math.max(rect.height, 1);
  if (selected) {
    const item = items.find((entry) => entry.id === selected.itemId);
    const box = item?.boxes[selected.boxIndex];
    if (item && box) {
      for (const [edge, x, y] of handles(box)) {
        if (Math.abs(at.x - x) <= handleX && Math.abs(at.y - y) <= handleY) {
          return { item, boxIndex: selected.boxIndex, edge };
        }
      }
    }
  }
  let best: { item: GarmentItem; boxIndex: number; edge: string; area: number } | null = null;
  for (const item of items) {
    item.boxes.forEach((box, boxIndex) => {
      const [x1, y1, x2, y2] = box;
      if (at.x < x1 || at.x > x2 || at.y < y1 || at.y > y2) return;
      const area = (x2 - x1) * (y2 - y1);
      if (!best || area < best.area) best = { item, boxIndex, edge: '', area };
    });
  }
  return best;
}

function movedBox(drag: Drag, at: { x: number; y: number }) {
  const box = [...drag.origin];
  if (drag.kind === 'move') {
    const width = box[2] - box[0];
    const height = box[3] - box[1];
    let x1 = clamp(at.x - drag.dx, 0, 1 - width);
    let y1 = clamp(at.y - drag.dy, 0, 1 - height);
    return [round(x1), round(y1), round(x1 + width), round(y1 + height)];
  }
  if (drag.edge.includes('w')) box[0] = clamp(at.x, 0, box[2] - 0.01);
  if (drag.edge.includes('e')) box[2] = clamp(at.x, box[0] + 0.01, 1);
  if (drag.edge.includes('n')) box[1] = clamp(at.y, 0, box[3] - 0.01);
  if (drag.edge.includes('s')) box[3] = clamp(at.y, box[1] + 0.01, 1);
  return box.map(round);
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function round(value: number) {
  return Math.round(value * 10000) / 10000;
}
